import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ImportPage from './pages/ImportPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AuditPage from './pages/AuditPage';
import TargetsPage from './pages/TargetsPage';
import PerformancePage from './pages/PerformancePage';
import ClientPortalPage from './pages/ClientPortalPage';
import TierConfigPage from './pages/TierConfigPage';
import StaffManagementPage from './pages/StaffManagementPage';
import DealerMasterPage from './pages/DealerMasterPage';
import RMMasterPage from './pages/RMMasterPage';
import TargetDefinitionPage from './pages/TargetDefinitionPage';
import ShortfallOverviewPage from './pages/ShortfallOverviewPage';
import ApprovalsPage from './pages/ApprovalsPage';
import MeetingLogPage from './pages/MeetingLogPage';
import MyTargetsPage from './pages/MyTargetsPage';
import TeamPerformancePage from './pages/TeamPerformancePage';
import IncentiveConfigPage from './pages/IncentiveConfigPage';
import IncentiveReportsPage from './pages/IncentiveReportsPage';
import IncentivePage from './pages/IncentivePage';
import MyIncentivePage from './pages/MyIncentivePage';
import MyDealersPage from './pages/MyDealersPage';
import DataFetchPage from './pages/DataFetchPage';
import WhatsAppPage from './pages/WhatsAppPage';
import CallHistoryPage from './pages/CallHistoryPage';
import ProfileSettingsPage from './pages/ProfileSettingsPage';
import MarketHolidaysPage from './pages/MarketHolidaysPage';
import './App.css';

const ProtectedRoute = ({ children, allowedRoles, allowedEmails }) => {
  const { user, loading, hasRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check role-based access
  const hasRoleAccess = !allowedRoles || hasRole(allowedRoles);
  
  // Check email-based access
  const hasEmailAccess = allowedEmails && user?.email && allowedEmails.includes(user.email);
  
  // Allow if user has role access OR email access
  if (!hasRoleAccess && !hasEmailAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      
      {/* Client Portal - Separate from staff routes */}
      <Route path="/client-portal" element={<ClientPortalPage />} />
      <Route path="/client-portal/*" element={<ClientPortalPage />} />
      
      {/* Reset Password - Public route */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route
          path="/import"
          element={
            <ProtectedRoute 
              allowedRoles={['principal', 'business_head', 'backoffice']}
              allowedEmails={['kiran@ispravawealth.com']}
            >
              <ImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/data-fetch"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head', 'backoffice']}>
              <DataFetchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/whatsapp"
          element={
            <ProtectedRoute allowedRoles={['principal']}>
              <WhatsAppPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/call-history"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head', 'rm', 'dealer']}>
              <CallHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head', 'rm', 'dealer', 'backoffice']}>
              <ProfileSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head', 'rm', 'dealer']}>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/targets"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head', 'rm', 'dealer']}>
              <TargetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/target-definition"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head']}>
              <TargetDefinitionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/market-holidays"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head']}>
              <MarketHolidaysPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-targets"
          element={
            <ProtectedRoute allowedRoles={['rm', 'dealer']}>
              <MyTargetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shortfall-overview"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head']}>
              <ShortfallOverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team-performance"
          element={
            <ProtectedRoute allowedRoles={['rm']}>
              <TeamPerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-dealers"
          element={
            <ProtectedRoute allowedRoles={['rm']}>
              <MyDealersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/approvals"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head']}>
              <ApprovalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/meeting-log"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head', 'rm', 'dealer']}>
              <MeetingLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-incentive"
          element={
            <ProtectedRoute allowedRoles={['rm', 'dealer']}>
              <MyIncentivePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head', 'rm', 'dealer']}>
              <PerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <ProtectedRoute allowedRoles={['principal']}>
              <AuditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tier-config"
          element={
            <ProtectedRoute allowedRoles={['principal']}>
              <TierConfigPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/incentives"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head']}>
              <IncentivePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/incentive-config"
          element={
            <ProtectedRoute allowedRoles={['principal']}>
              <IncentiveConfigPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/incentive-reports"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head']}>
              <IncentiveReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff"
          element={
            <ProtectedRoute allowedRoles={['principal']}>
              <StaffManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rms"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head']}>
              <RMMasterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dealers"
          element={
            <ProtectedRoute allowedRoles={['principal', 'business_head']}>
              <DealerMasterPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#0F172A',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#F8FAFC'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
